import SequencerNodeUtils from "models/audioEditor/SequencerNodeUtils";
import BreathingAnalysisHelper from "models/audioEditor/BreathingAnalysisHelper";
import VoiceActivityDetectionManager from "models/audioEditor/VoiceActivityDetectionManager";

/**
 * Example showing how to use the safe trim fallback system with optional breathing analysis.
 * This demonstrates the architectural separation where you can choose to use breathing analysis or not.
 */
export default class CutAnalysisExample {
    
    /**
     * Basic cut analysis using only reliable Silero VAD (no breathing analysis)
     * This is the most reliable approach - just uses the proven VAD system.
     */
    static performBasicCutAnalysis(arrayOfSequencerNodes, options = {}) {
        const {
            leftBuffer = 80,        // 80ms buffer
            rightBuffer = 80,       // 80ms buffer  
            leftSilence = 0,        // No additional silence padding
            rightSilence = 0,       // No additional silence padding
            useSpeechOptimized = false  // Use standard or speech-optimized fallbacks
        } = options;

        // Generate fallback parameters (relies only on VAD)
        const fallbackParams = useSpeechOptimized ?
            SequencerNodeUtils.getInstance().generateSpeechOptimizedSafeTrimFallbackParameters(
                leftBuffer, rightBuffer, leftSilence, rightSilence
            ) :
            SequencerNodeUtils.getInstance().generateSafeTrimFallbackParameters(
                leftBuffer, rightBuffer, leftSilence, rightSilence
            );

        // Perform cut analysis using only VAD
        const result = SequencerNodeUtils.getInstance().getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodesWithFallback(
            arrayOfSequencerNodes, 
            fallbackParams
        );

        return {
            ...result,
            analysisType: 'basic_vad_only',
            breathingAnalysisUsed: false
        };
    }

    /**
     * Enhanced cut analysis with optional breathing detection
     * This adds breathing pause detection on top of the reliable VAD system.
     */
    static performEnhancedCutAnalysis(arrayOfSequencerNodes, options = {}) {
        const {
            leftBuffer = 80,
            rightBuffer = 80,
            leftSilence = 0,
            rightSilence = 0,
            useSpeechOptimized = false,
            enableBreathingAnalysis = true,     // Optional breathing analysis
            breathingAnalysisMode = 'conservative'  // 'conservative' or 'enhanced'
        } = options;

        // First, get the basic cut analysis (reliable VAD only)
        const basicResult = this.performBasicCutAnalysis(arrayOfSequencerNodes, {
            leftBuffer, rightBuffer, leftSilence, rightSilence, useSpeechOptimized
        });

        // If basic analysis failed or breathing analysis is disabled, return basic result
        if (!basicResult.nodes || basicResult.nodes.length === 0 || !enableBreathingAnalysis) {
            return basicResult;
        }

        // Add optional breathing analysis to the successful cut
        const breathingResults = [];
        
        basicResult.nodes.forEach(node => {
            let breathingAnalysis = null;
            
            if (breathingAnalysisMode === 'enhanced') {
                // Use the more sophisticated breathing analysis
                const vadResults = node.getSequencer().getAudioBufferCache().getVoiceActivityDetectionResultsForAudioUrl(
                    node.getAudioSegment().getAudioUrl(),
                    VoiceActivityDetectionManager.VAD_CONFIGS.BALANCED // Use unified balanced config for all use cases
                );
                
                if (vadResults && vadResults.silenceSegments) {
                    breathingAnalysis = BreathingAnalysisHelper.getInstance().analyzeBreathingPausesFromVAD(
                        vadResults,
                        node.getAudioSegment().getStartTime() / 1000,
                        node.getAudioSegment().getEndTime() / 1000
                    );
                }
            } else {
                // Use conservative breathing analysis (default)
                breathingAnalysis = node.findPotentialBreathingPauses({
                    conservativeMode: true,
                    minBreathDuration: 400
                }, VoiceActivityDetectionManager.VAD_CONFIGS.BALANCED); // Use unified balanced config for all use cases
            }
            
            if (breathingAnalysis && breathingAnalysis.totalPotentialBreaths > 0) {
                breathingResults.push({
                    node: node,
                    analysis: breathingAnalysis
                });
            }
        });

        return {
            ...basicResult,
            analysisType: 'enhanced_with_breathing',
            breathingAnalysisUsed: true,
            breathingAnalysisMode: breathingAnalysisMode,
            breathingResults: breathingResults,
            breathingDetected: breathingResults.length > 0
        };
    }

    /**
     * Smart cut analysis that chooses the best approach based on content
     * This demonstrates how you can decide whether to use breathing analysis or not.
     */
    static performSmartCutAnalysis(arrayOfSequencerNodes, options = {}) {
        const {
            autoDetectContentType = true,
            forceBreathingAnalysis = false,
            ...otherOptions
        } = options;

        // Decide whether to use breathing analysis based on content
        let useBreathingAnalysis = forceBreathingAnalysis;
        
        if (autoDetectContentType && !forceBreathingAnalysis) {
            // Auto-detect if this looks like speech content that would benefit from breathing analysis
            const speechNodes = arrayOfSequencerNodes.filter(node => 
                node.containsVoiceActivity(VoiceActivityDetectionManager.VAD_CONFIGS.BALANCED) // Use unified balanced config for all use cases
            );
            
            // If more than 50% of nodes contain voice activity, it's likely speech
            const speechRatio = speechNodes.length / arrayOfSequencerNodes.length;
            useBreathingAnalysis = speechRatio > 0.5;
        }

        if (useBreathingAnalysis) {
            console.log('Using enhanced cut analysis with breathing detection');
            return this.performEnhancedCutAnalysis(arrayOfSequencerNodes, {
                ...otherOptions,
                enableBreathingAnalysis: true,
                breathingAnalysisMode: 'conservative'  // Start with conservative
            });
        } else {
            console.log('Using basic cut analysis (VAD only)');
            return this.performBasicCutAnalysis(arrayOfSequencerNodes, otherOptions);
        }
    }

    /**
     * Example usage patterns
     */
    static demonstrateUsage(arrayOfSequencerNodes) {
        console.log('=== Cut Analysis Examples ===');

        // Example 1: Basic reliable cutting (VAD only)
        console.log('\n1. Basic VAD-only cutting:');
        const basicResult = this.performBasicCutAnalysis(arrayOfSequencerNodes, {
            useSpeechOptimized: true
        });
        
        if (basicResult.nodes.length > 0) {
            console.log(`✓ Cut successful using: ${basicResult.usedParameterSet.description}`);
            console.log(`  Quality: ${basicResult.usedParameterSetIndex === 0 ? 'Optimal' : 'Fallback'}`);
        } else {
            console.log('✗ Cut failed with all parameter sets');
        }

        // Example 2: Enhanced cutting with breathing analysis
        console.log('\n2. Enhanced cutting with breathing analysis:');
        const enhancedResult = this.performEnhancedCutAnalysis(arrayOfSequencerNodes, {
            useSpeechOptimized: true,
            enableBreathingAnalysis: true,
            breathingAnalysisMode: 'conservative'
        });
        
        if (enhancedResult.nodes.length > 0) {
            console.log(`✓ Cut successful using: ${enhancedResult.usedParameterSet.description}`);
            console.log(`  Breathing analysis: ${enhancedResult.breathingAnalysisUsed ? 'Enabled' : 'Disabled'}`);
            if (enhancedResult.breathingDetected) {
                console.log(`  Breathing detected in ${enhancedResult.breathingResults.length} nodes`);
            }
        }

        // Example 3: Smart auto-detection
        console.log('\n3. Smart auto-detection:');
        const smartResult = this.performSmartCutAnalysis(arrayOfSequencerNodes, {
            autoDetectContentType: true,
            useSpeechOptimized: true
        });
        
        console.log(`✓ Analysis type: ${smartResult.analysisType}`);
        console.log(`  Breathing analysis: ${smartResult.breathingAnalysisUsed ? 'Used' : 'Not used'}`);

        return {
            basic: basicResult,
            enhanced: enhancedResult,
            smart: smartResult
        };
    }
} 